
import React, { createContext, useState, useEffect, useMemo } from 'react';

interface PriceVisibilityContextType {
  isPriceVisible: boolean;
  unlockPrices: () => void;
}

export const PriceVisibilityContext = createContext<PriceVisibilityContextType>({
  isPriceVisible: false,
  unlockPrices: () => {},
});

const EXPIRY_TIME = 15 * 24 * 60 * 60 * 1000; // 15 days in milliseconds

export const PriceVisibilityProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isPriceVisible, setIsPriceVisible] = useState(false);

  useEffect(() => {
    // On initial load, check if the user has already unlocked prices.
    const unlockData = localStorage.getItem('meragi_prices_unlocked');
    if (unlockData) {
      if (unlockData === 'true') {
        const newExpiry = Date.now() + EXPIRY_TIME;
        localStorage.setItem('meragi_prices_unlocked', newExpiry.toString());
        setIsPriceVisible(true);
      } else {
        const expiry = parseInt(unlockData, 10);
        if (Date.now() < expiry) {
          setIsPriceVisible(true);
        } else {
          localStorage.removeItem('meragi_prices_unlocked');
          setIsPriceVisible(false);
        }
      }
    }
  }, []);

  const unlockPrices = () => {
    // Persist the unlocked state in localStorage and update the context state.
    const expiry = Date.now() + EXPIRY_TIME;
    localStorage.setItem('meragi_prices_unlocked', expiry.toString());
    setIsPriceVisible(true);
  };

  const value = useMemo(() => ({
    isPriceVisible,
    unlockPrices,
  }), [isPriceVisible]);

  return (
    <PriceVisibilityContext.Provider value={value}>
      {children}
    </PriceVisibilityContext.Provider>
  );
};
